/**
 * RBAC_V2 permission catalog — the single frontend source of truth for the
 * 25 grantable-surface codes (decisions §4/§34, architecture R5; catalog
 * expanded from 16 → 25 to make every remaining sidebar section grantable —
 * only /admin (Admin View) itself stays Super-Admin-only). Imported
 * by `useCurrentUser`, the sidebar (`useSidebarNav`/`groupsConfig`), route
 * guards (`RequirePermission`), and the v2 Users admin page.
 *
 * Runtime truth for the role-editor checklist still comes from the backend
 * (GET permissions) — this module is the GATING truth + the legacy flag-off
 * shim, kept in lockstep with the server's own catalog
 * (server/src/config/permissionMap.ts) by the cross-package parity test
 * server/src/__tests__/config/catalogParity.test.ts.
 */

export const PERMISSION_CODES = [
  'dashboard',
  'tenant_tab',
  'actions',
  'task_v2',
  'cms',
  'content',
  'flex_components',
  'milestones',
  'consumer_360',
  'member_import',
  'insights_manager',
  'insights_client',
  'publish',
  'promote',
  'prod_option',
  'super_editor_publisher',
  'config',
  'data_integrations',
  'pdf_to_html',
  'notifications',
  'operations',
  'financial',
  'build_in_flex',
  'administration',
  'feature_flags',
  'hide_chrome',
] as const;

export type PermissionCode = typeof PERMISSION_CODES[number];

/** Permissions that REMOVE UI rather than grant it — never implied by super-admin. */
export const INVERSE_PERMISSIONS: ReadonlySet<PermissionCode> = new Set(['hide_chrome']);

/**
 * Full-access set for synthetic/local-dev identities: every grantable
 * permission except the inverse one (`hide_chrome`) and the client-pairing
 * one (`insights_client`, which only matters paired with `hide_chrome`).
 */
export const ALL_GRANTABLE_PERMISSIONS = PERMISSION_CODES.filter(
  (c) => c !== 'hide_chrome' && c !== 'insights_client',
);

export type PermissionGroup =
  | 'Workspaces'
  | 'Insights'
  | 'Publishing & Promotion'
  | 'Admin & Operations'
  | 'Layout';

/** Role-editor grouping + copy (architecture R5). `order` sorts within its group. */
export const PERMISSION_META: Record<
  PermissionCode,
  { label: string; description: string; group: PermissionGroup; order: number }
> = {
  dashboard: {
    label: 'Dashboard',
    description: 'Tenant dashboard overview (admin widgets stay super-admin).',
    group: 'Workspaces',
    order: 1,
  },
  tenant_tab: {
    label: 'Tenant',
    description:
      'Tenant settings: Info, Onboarding, Attributes, Import/Export (Wallet Categories, FIS Config, Tenant Tables stay super-admin).',
    group: 'Workspaces',
    order: 2,
  },
  actions: {
    label: 'Actions',
    description:
      'All Actions, Library, Adventures, and action editors (trivia, questionnaires, sweepstakes, terms of service, relationships).',
    group: 'Workspaces',
    order: 3,
  },
  task_v2: {
    label: 'Task V2',
    description:
      'Task V2 section: V2 Metrics, V2 Completion Criteria, Library V2 (MasterTenant), and a tenant’s imported V2 actions. Independent of the isTaskV2Enabled tenant attribute, which controls whether a given tenant’s actions run on the V2 model — this permission only gates who can see the V2 authoring surfaces.',
    group: 'Workspaces',
    order: 3.5,
  },
  cms: {
    label: 'Content (Old)',
    description: 'Legacy CMS: components and HTML templates (the Content (Old) area).',
    group: 'Workspaces',
    order: 4,
  },
  content: {
    label: 'Content',
    description: 'Storyblok content: CMS Components, FAQs, Cards, Agreements.',
    group: 'Workspaces',
    order: 5,
  },
  flex_components: {
    label: 'Flex Components',
    description: 'Flex component families and palette.',
    group: 'Workspaces',
    order: 6,
  },
  milestones: {
    label: 'Milestones',
    description: 'Milestone rules.',
    group: 'Workspaces',
    order: 7,
  },
  consumer_360: {
    label: 'Consumer 360',
    description: 'Consumer lookup, detail, and transactions.',
    group: 'Workspaces',
    order: 8,
  },
  member_import: {
    label: 'Member Import',
    description: 'Member import runs and history.',
    group: 'Workspaces',
    order: 9,
  },
  config: {
    label: 'Config (Cohorts)',
    description: 'Config section, Cohorts only; the other config items stay super-admin.',
    group: 'Workspaces',
    order: 10,
  },
  data_integrations: {
    label: 'Data Integrations',
    description: 'Data Integrations section: Sources & Versions, Canonical Mapping, Lookups, Dimensions.',
    group: 'Admin & Operations',
    order: 1,
  },
  pdf_to_html: {
    label: 'PDF to HTML',
    description:
      'PDF to HTML section (MasterTenant only) — converts PDF documents into HTML content templates.',
    group: 'Admin & Operations',
    order: 2,
  },
  notifications: {
    label: 'Notifications',
    description: 'Notifications section: event types, rules, messages, categories, channels.',
    group: 'Admin & Operations',
    order: 3,
  },
  operations: {
    label: 'Operations',
    description:
      'Operations section: Pipeline Monitor, Job History, Card Funding, Card Operations, Health Program, Consumer Dashboard, Activity Feed, Go-Live Checklist.',
    group: 'Admin & Operations',
    order: 4,
  },
  financial: {
    label: 'Financial',
    description: 'Financial section: Reconciliation.',
    group: 'Admin & Operations',
    order: 5,
  },
  build_in_flex: {
    label: 'Build in Flex',
    description:
      'External link to the Flex component builder (sunny-flex.vercel.app) — no in-portal page of its own.',
    group: 'Admin & Operations',
    order: 6,
  },
  administration: {
    label: 'Administration',
    description: 'Administration section: API Keys, Bulk Operations, File Validation.',
    group: 'Admin & Operations',
    order: 7,
  },
  feature_flags: {
    label: 'Feature Flags',
    description: 'Feature Flags section — Mixpanel flag state evaluated for the tenant.',
    group: 'Admin & Operations',
    order: 8,
  },
  insights_manager: {
    label: 'Insights — manager',
    description: 'Insights dashboards with full portal chrome.',
    group: 'Insights',
    order: 1,
  },
  insights_client: {
    label: 'Insights — client',
    description: 'Insights for external clients; usually paired with Hide chrome.',
    group: 'Insights',
    order: 2,
  },
  publish: {
    label: 'Publish',
    description:
      'Publish page and publish/apply API routes, layered on top of tenant write access and the env fence.',
    group: 'Publishing & Promotion',
    order: 1,
  },
  promote: {
    label: 'Promote',
    description: 'View/run promotions into non-prod environments.',
    group: 'Publishing & Promotion',
    order: 2,
  },
  prod_option: {
    label: 'Prod option',
    description:
      'PROD becomes visible/targetable (env pickers, deploy matrix); with Promote, allows promoting into PROD.',
    group: 'Publishing & Promotion',
    order: 3,
  },
  super_editor_publisher: {
    label: 'Super Editor & Publisher',
    description:
      'Bypasses the environment write fence: edit and publish on ANY environment, including read-only ones (e.g. prod). Every Super Admin already has this automatically — grant it standalone to give the same bypass to a non-super-admin without giving them full Super Admin access.',
    group: 'Publishing & Promotion',
    order: 4,
  },
  hide_chrome: {
    label: 'Hide chrome',
    description:
      'INVERSE permission: hides the top-bar chrome (env/version picker, search, Publish) for analytics clients — the sidebar and user account block remain fully visible; the user lands directly on Insights.',
    group: 'Layout',
    order: 1,
  },
};

/**
 * Sidebar group key → gate. `'super-admin'` mirrors today's `requiredRole:
 * 'admin'` groups (decisions §4 — not grantable); an array is an `anyOf`
 * permission check. Every key in `SIDEBAR_GROUPS`/`SIDEBAR_EXTERNAL_ITEMS`
 * (src/pages/Layout/sidebar/groupsConfig.ts) MUST have an entry here —
 * `useSidebarNav` dev-warns otherwise.
 */
export type SectionGate = PermissionCode[] | 'super-admin';

export const SECTION_GATES: Record<string, SectionGate> = {
  dashboard: ['dashboard'],
  tenant: ['tenant_tab'],
  actions: ['actions'],
  'task-v2': ['task_v2'],
  'data-integrations': ['data_integrations'],
  cms: ['cms'],
  'pdf-to-html': ['pdf_to_html'],
  'storyblok-content': ['content'],
  'flex-components': ['flex_components'],
  notifications: ['notifications'],
  'consumer-360': ['consumer_360'],
  'member-import': ['member_import'],
  operations: ['operations'],
  financial: ['financial'],
  // Hidden under v2 via `hiddenWhenV2` (replaced by `insights` below, R12) —
  // `canSeeGroup` short-circuits on that flag before this is ever read.
  // Kept so the "every group has a gate" completeness check still holds.
  analytics: 'super-admin',
  milestones: ['milestones'],
  'program-year': ['config'],
  administration: ['administration'],
  'environment-promotion': ['promote'],
  'feature-flags': ['feature_flags'],
  'build-in-flex': ['build_in_flex'],
  // NEW under v2 (R12), takes the `analytics` slot. The actual tenant route
  // (paths.insights + the observability.tsx route entry) lands in WP6 — this
  // mapping is inert (no route references the group yet) until that lands.
  insights: ['insights_manager', 'insights_client'],
};

/**
 * Flag-off shim — one entry per code, reproducing TODAY's gate exactly so
 * rewired call sites (`hasPermission`) never need to branch on the flag.
 * See architecture R4 / frontend design §1.3.
 */
export const LEGACY_SHIM: Record<
  PermissionCode,
  (u: { isSuperAdmin: boolean; hasRole: (role: string) => boolean }) => boolean
> = {
  dashboard: () => true,
  tenant_tab: () => true,
  actions: () => true,
  cms: () => true,
  content: () => true,
  milestones: () => true,
  consumer_360: () => true,
  member_import: () => true,
  config: () => true,
  // Publish is unenforced today (RequireRole's ENFORCED_ROLES excludes
  // `publisher`) — everyone keeps it at cutover (decisions §31).
  publish: () => true,
  flex_components: (u) => u.isSuperAdmin || u.hasRole('flex'),
  promote: (u) => u.isSuperAdmin || u.hasRole('promoter'),
  prod_option: (u) => u.isSuperAdmin || u.hasRole('prod_promoter'),
  // Flag-off: mirrors legacy hasRole('super-editor-publisher') directly —
  // this permission's whole reason for existing is to be independent of
  // isSuperAdmin, so unlike promote/prod_option above it does NOT also
  // check isSuperAdmin here (flag-on's bypass check already covers Super
  // Admins separately, see userHasFenceBypass).
  super_editor_publisher: (u) => u.hasRole('super-editor-publisher'),
  // Task V2 is gated by the `taskv2` capability role today (index.ts's
  // requireRole('taskv2') mounts + the sidebar group's requiredRole) — mirrors
  // `flex_components`/`promote` above.
  task_v2: (u) => u.isSuperAdmin || u.hasRole('taskv2'),
  // PDF to HTML shipped Super-Admin-locked (route RequireSuperAdmin wrapper +
  // sidebar adminOnly) — flag-off must stay that way. Flag-on, the wrapper's
  // `unlessPermission` escape lets pdf_to_html holders through.
  pdf_to_html: (u) => u.isSuperAdmin,
  data_integrations: (u) => u.isSuperAdmin,
  notifications: (u) => u.isSuperAdmin,
  operations: (u) => u.isSuperAdmin,
  financial: (u) => u.isSuperAdmin,
  build_in_flex: (u) => u.isSuperAdmin,
  administration: (u) => u.isSuperAdmin,
  feature_flags: (u) => u.isSuperAdmin,
  // Analytics is admin-only today.
  insights_manager: (u) => u.isSuperAdmin,
  insights_client: () => false,
  hide_chrome: () => false,
};

/**
 * Ordered fallback chain for a denied route / the tenant index redirect —
 * replaces the hard-coded `.../dashboard` target (RequireRole.tsx:61) with
 * the first surface this user actually holds. Flag off: `dashboard`'s shim
 * is always `true`, so this degenerates to today's target with no branch
 * needed at call sites.
 */
export const FALLBACK_SURFACES: Array<[PermissionCode, (tenantCode: string) => string]> = [
  ['dashboard', (tc) => `/tenants/${tc}/dashboard`],
  ['insights_client', (tc) => `/tenants/${tc}/insights`],
  ['insights_manager', (tc) => `/tenants/${tc}/insights`],
  ['tenant_tab', (tc) => `/tenants/${tc}/settings`],
  ['actions', (tc) => `/tenants/${tc}/actions`],
  ['cms', (tc) => `/tenants/${tc}/cms-components`],
  ['content', (tc) => `/tenants/${tc}/storyblok-content/cms-components`],
  ['flex_components', (tc) => `/tenants/${tc}/flex-components`],
  ['milestones', (tc) => `/tenants/${tc}/program-year/milestones`],
  ['consumer_360', (tc) => `/tenants/${tc}/users`],
  ['member_import', (tc) => `/tenants/${tc}/member-import`],
  ['config', (tc) => `/tenants/${tc}/program-year/cohorts`],
  ['promote', (tc) => `/tenants/${tc}/environment-promotion`],
  // publish joined the catalog late (addendum §30) — without an entry here a
  // publish-only role would land on /access-denied despite holding a surface.
  ['publish', (tc) => `/tenants/${tc}/publish`],
  // Catalog-expansion codes (formerly hard-locked to super-admin) — appended
  // after the original list rather than interleaved, so existing fallback
  // ordering for the first 14 codes is untouched. `build_in_flex` is an
  // EXTERNAL link (no in-app route) and deliberately has no entry here.
  ['task_v2', (tc) => `/tenants/${tc}/task-v2/actions`],
  ['data_integrations', (tc) => `/tenants/${tc}/integrations/sources`],
  ['pdf_to_html', (tc) => `/tenants/${tc}/pdf-to-html`],
  ['notifications', (tc) => `/tenants/${tc}/notifications`],
  ['operations', (tc) => `/tenants/${tc}/pipelines`],
  ['financial', (tc) => `/tenants/${tc}/financial-reconciliation`],
  ['administration', (tc) => `/tenants/${tc}/api-keys`],
  ['feature_flags', (tc) => `/tenants/${tc}/feature-flags`],
];

/**
 * First surface this user is granted, for a given tenant. `hide_chrome`
 * holders are pinned straight to Insights (decisions §11) — but ONLY when
 * they actually hold an insights surface: pinning unconditionally sends a
 * misconfigured role (hide_chrome without insights_*) into a redirect dead
 * end on a page that denies them, i.e. a blank portal with no escape
 * (adversarial review P1). Without the pairing they fall through to their
 * real grants, ending at /access-denied like any other zero-surface user.
 * Takes a minimal duck-typed user (not the full `CurrentUser`) so this
 * module never imports from `useCurrentUser`, which imports FROM here —
 * avoids a circular dependency.
 */
export function firstGrantedSurfacePath(
  user: { hasPermission: (code: PermissionCode) => boolean },
  tenantCode: string,
): string {
  if (
    user.hasPermission('hide_chrome') &&
    (user.hasPermission('insights_manager') || user.hasPermission('insights_client'))
  ) {
    return `/tenants/${tenantCode}/insights`;
  }
  for (const [code, toPath] of FALLBACK_SURFACES) {
    if (user.hasPermission(code)) return toPath(tenantCode);
  }
  return '/access-denied';
}
